import { useMemo } from 'react';
import { Flex, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  isDeprecatedMaintenanceScopeDimension,
  type MaintenanceSlaInterventionMode,
  type MaintenanceSlaSavedScopeRule,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

export function useMaintenanceSlaScopeColumns(
  labelFor: (rule: MaintenanceSlaSavedScopeRule) => string,
  interventionMode: MaintenanceSlaInterventionMode | null,
) {
  const { t } = useTranslation('sla');
  const dash = t('oems.maintenance.dash');

  return useMemo(() => {
    const columns = [
      {
        title: t('oems.maintenance.dimensionLabel'),
        key: 'dimension',
        render: (_: unknown, rule: MaintenanceSlaSavedScopeRule) => {
          const deprecated = isDeprecatedMaintenanceScopeDimension(
            rule.dimension,
          );
          return (
            <Flex align="center" gap={8}>
              <span>
                {deprecated
                  ? rule.dimension
                  : t(`oems.maintenance.dimension.${rule.dimension}`)}
              </span>
              {deprecated ? (
                <Tooltip title="This scope dimension was retired on 2026-10-08; edit is disabled">
                  <Tag color="default">Deprecated</Tag>
                </Tooltip>
              ) : null}
            </Flex>
          );
        },
      },
      {
        title: t('oems.maintenance.effectLabel'),
        key: 'effect',
        render: (_: unknown, rule: MaintenanceSlaSavedScopeRule) =>
          t(`oems.maintenance.effect.${rule.effect}`),
      },
      {
        title: t('oems.maintenance.value'),
        key: 'value',
        render: (_: unknown, rule: MaintenanceSlaSavedScopeRule) =>
          labelFor(rule),
      },
    ];

    if (interventionMode === 'BOTH') {
      columns.splice(3, 0, {
        title: t('oems.maintenance.modeLabel'),
        key: 'interventionMode',
        render: (_: unknown, rule: MaintenanceSlaSavedScopeRule) =>
          rule.interventionMode
            ? t(`oems.maintenance.mode.${rule.interventionMode}`)
            : dash,
      });
    }

    return columns;
  }, [labelFor, t, dash, interventionMode]);
}
